import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { updatePatientMedicalInfo } from "../../networkCalls";
import {
  patientBasicInfoType,
  patientMedicalInfoEntityType,
} from "../Profile/PatientTypes";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import dayjs, { Dayjs } from "dayjs";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox } from "../../../components/ui/checkbox";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Textarea } from "../../../components/ui/textarea";
import { Separator } from "../../../components/ui/separator";
import { MonthCalendar } from "../../../components/ui/month-calendar";
import { Skeleton } from "../../../components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { CalendarDays, Save } from "lucide-react";
import { cn } from "../../../lib/utils";

var _ = require("lodash");

const Gender = [
  "MALE".toString(),
  "FEMALE".toString(),
  "OTHER".toString(),
  "UNK".toString(),
] as const;
const BloodGroup = [
  "A+".toString(),
  "B+".toString(),
  "AB+".toString(),
  "O+".toString(),
  "A-".toString(),
  "B-".toString(),
  "AB-".toString(),
  "O-".toString(),
  "UNK".toString(),
] as const;

export const PatientMedicalInfoSchema = z.object({
  id: z.string().optional(),
  diabetesMellitus: z.boolean().default(false).optional(),
  hypertension: z.boolean().default(false).optional(),
  ishaemicHeartDisease: z.boolean().default(false).optional(),
  respiratoryDisorders: z.boolean().default(false).optional(),
  allergies: z.boolean().default(false).optional(),
  previousSurgeries: z.boolean().default(false).optional(),
  bloodDyscrasias: z.boolean().default(false).optional(),
  hypothyroidism: z.boolean().default(false).optional(),
  pregnancyAndRelatedIssues: z.boolean().default(false).optional(),
  psychiatricAilments: z.boolean().default(false).optional(),
  dateOfBirth: z
    .custom<Dayjs>((val) => dayjs.isDayjs(val), "Invalid date")
    .optional(),
  gender: z.enum(Gender).optional(),
  bloodGroup: z.enum(BloodGroup).optional(),
  other: z.string().optional(),
});

type MedicalValues = z.infer<typeof PatientMedicalInfoSchema>;

const CONDITIONS: { name: keyof MedicalValues; label: string }[] = [
  { name: "diabetesMellitus", label: "Diabetes Mellitus" },
  { name: "hypertension", label: "Hypertension" },
  { name: "ishaemicHeartDisease", label: "Ishaemic Heart Disease" },
  { name: "respiratoryDisorders", label: "Respiratory Disorders" },
  { name: "allergies", label: "Allergies" },
  { name: "previousSurgeries", label: "Previous Surgeries" },
  { name: "bloodDyscrasias", label: "Blood Dyscrasias" },
  { name: "hypothyroidism", label: "Hypothyroidism" },
  { name: "pregnancyAndRelatedIssues", label: "Pregnancy & Related Issues" },
  { name: "psychiatricAilments", label: "Psychiatric Ailments" },
];

// Map the shared parent state into react-hook-form values. dateOfBirth may
// arrive as an ISO string or a Dayjs; normalize to a local Dayjs (or undefined).
function toFormValues(state: MedicalValues): MedicalValues {
  return {
    id: state.id,
    diabetesMellitus: state.diabetesMellitus ?? false,
    hypertension: state.hypertension ?? false,
    ishaemicHeartDisease: state.ishaemicHeartDisease ?? false,
    respiratoryDisorders: state.respiratoryDisorders ?? false,
    allergies: state.allergies ?? false,
    previousSurgeries: state.previousSurgeries ?? false,
    bloodDyscrasias: state.bloodDyscrasias ?? false,
    hypothyroidism: state.hypothyroidism ?? false,
    pregnancyAndRelatedIssues: state.pregnancyAndRelatedIssues ?? false,
    psychiatricAilments: state.psychiatricAilments ?? false,
    dateOfBirth: state.dateOfBirth
      ? dayjs.utc(state.dateOfBirth).local()
      : undefined,
    gender: state.gender,
    bloodGroup: state.bloodGroup,
    other: state.other,
  };
}

// Build a raw entity from submitted form values, used as a fallback when the
// PUT response comes back empty so the UI keeps showing what was just saved.
function buildEntityFromValues(
  values: MedicalValues,
  id: string | undefined
): patientMedicalInfoEntityType {
  return {
    id: id ?? null,
    diabetesMellitus: values.diabetesMellitus ?? false,
    hypertension: values.hypertension ?? false,
    ishaemicHeartDisease: values.ishaemicHeartDisease ?? false,
    respiratoryDisorders: values.respiratoryDisorders ?? false,
    allergies: values.allergies ?? false,
    previousSurgeries: values.previousSurgeries ?? false,
    bloodDyscrasias: values.bloodDyscrasias ?? false,
    hypothyroidism: values.hypothyroidism ?? false,
    pregnancyAndRelatedIssues: values.pregnancyAndRelatedIssues ?? false,
    psychiatricAilments: values.psychiatricAilments ?? false,
    dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null,
    gender: values.gender ?? null,
    bloodGroup: values.bloodGroup ?? null,
    other: values.other ?? null,
  };
}

export function PatientMedicalProfileForm({
  patientMedicalInfoState,
  isNew,
  updatePatientMedicalInfoState,
  patientIdState,
  patientBasicInfoState,
}: {
  patientMedicalInfoState: MedicalValues;
  isNew: boolean;
  updatePatientMedicalInfoState: any;
  patientIdState: string;
  patientBasicInfoState: patientBasicInfoType;
}) {
  const form = useForm<MedicalValues>({
    resolver: zodResolver(PatientMedicalInfoSchema),
    mode: "onChange",
    defaultValues: toFormValues(patientMedicalInfoState),
  });

  // Keep the form synced as the patient loads / updates; also resets the dirty
  // flag after a save (the parent state changes once saved).
  useEffect(() => {
    form.reset(toFormValues(patientMedicalInfoState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientMedicalInfoState]);

  const {
    formState: { isDirty, isValid, isSubmitting },
  } = form;

  const [dobOpen, setDobOpen] = useState(false);

  function onSubmit(values: MedicalValues) {
    if (patientIdState == null) {
      return;
    }
    updatePatientMedicalInfo(patientIdState, values)
      .then((updatedMedicalInfo) => {
        // PUT can return an empty body; fall back to the values we just saved.
        const saved =
          updatedMedicalInfo && updatedMedicalInfo.id ? updatedMedicalInfo : null;
        const entity =
          saved ?? buildEntityFromValues(values, patientMedicalInfoState.id);
        updatePatientMedicalInfoState(entity, patientIdState);
        toast.success("Updated Patient Medical Info");
      })
      .catch(() => {
        toast.error("Error saving patient medical info. please try again");
      });
  }

  if (patientBasicInfoState.id === null) {
    return (
      <Card className="w-full">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Medical Information
          {isNew && <Badge variant="secondary">New</Badge>}
        </CardTitle>
        <CardDescription>
          Conditions, demographics, and clinical notes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Conditions */}
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">
                  Medical Conditions
                </h3>
                <Separator className="mt-2" />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {CONDITIONS.map((condition) => (
                  <FormField
                    key={condition.name}
                    control={form.control}
                    name={condition.name}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-3">
                        <FormControl>
                          <Checkbox
                            checked={Boolean(field.value)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm font-normal">
                          {condition.label}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </section>

            {/* Demographics */}
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">
                  Demographics
                </h3>
                <Separator className="mt-2" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Date of Birth</FormLabel>
                      <Popover open={dobOpen} onOpenChange={setDobOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "justify-start text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarDays className="mr-2 h-4 w-4" />
                              {field.value
                                ? dayjs(field.value).format("DD-MM-YYYY")
                                : "Pick a date"}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2" align="start">
                          <MonthCalendar
                            selected={field.value ?? undefined}
                            disableFuture
                            onSelect={(date) => {
                              field.onChange(date);
                              setDobOpen(false);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Gender</FormLabel>
                      <Select
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Gender.map((slot) => (
                            <SelectItem key={slot} value={slot}>
                              {slot}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bloodGroup"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Blood Group</FormLabel>
                      <Select
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select blood group" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {BloodGroup.map((slot) => (
                            <SelectItem key={slot} value={slot}>
                              {slot}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            {/* Notes */}
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">
                  Additional Notes
                </h3>
                <Separator className="mt-2" />
              </div>
              <FormField
                control={form.control}
                name="other"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Other Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any other information we need to know about"
                        className="resize-none"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!isDirty || !isValid || isSubmitting}
              >
                <Save className="h-4 w-4" />
                Save Medical Info
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
